import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAppStore, type IAppStore } from '@/store';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction, type ScreenType } from '@/types/analytics';
import {
  getSavedTemplates,
  saveTemplates,
  type SavedTemplates,
} from '@/requests/cloud-storage/savedTemplates';
import useAuth from './useAuth';

const selector = (store: IAppStore) => store.toolId;

/**
 * Saved preset slugs, persisted as a hidden JSON file in the Drive tool folder.
 * The query (shared via React Query across every card) reads the list; toggling
 * updates the cache optimistically and writes the file back. Unauthorized users
 * are routed to registration.
 */
const useSavedTemplates = () => {
  const toolId = useAppStore(selector);
  const { isAuthorized } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['savedTemplates', toolId], [toolId]);

  const { data, isLoading } = useQuery<SavedTemplates | null>({
    queryKey,
    queryFn: () => getSavedTemplates({ toolId }),
    enabled: isAuthorized && Boolean(toolId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const savedSlugs = useMemo(() => new Set(data?.slugs ?? []), [data?.slugs]);

  const mutation = useMutation({
    mutationFn: (nextSlugs: string[]) => {
      const cached = queryClient.getQueryData<SavedTemplates | null>(queryKey);
      return saveTemplates({
        toolId,
        slugs: nextSlugs,
        fileId: cached?.fileId ?? null,
      });
    },
    onSuccess: (result) => {
      // null = save failed (already toasted) → resync from Drive.
      if (!result) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }
      if (result.fileId) {
        queryClient.setQueryData<SavedTemplates | null>(queryKey, (prev) =>
          prev ? { ...prev, fileId: result.fileId } : prev
        );
      }
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
  });

  const toggleSaved = useCallback(
    (slug: string, screenType?: ScreenType) => {
      if (!isAuthorized) {
        openRegistrationModal();
        return;
      }

      const current = queryClient.getQueryData<SavedTemplates | null>(queryKey);
      const currentSlugs = current?.slugs ?? [];
      const willSave = !currentSlugs.includes(slug);
      const nextSlugs = willSave
        ? [...currentSlugs, slug]
        : currentSlugs.filter((s) => s !== slug);

      queryClient.setQueryData<SavedTemplates | null>(queryKey, (prev) => ({
        slugs: nextSlugs,
        fileId: prev?.fileId ?? null,
      }));
      mutation.mutate(nextSlugs);

      sendAiGenScreenActionAnalytics({
        action: willSave ? AiGenScreenAction.SAVE : AiGenScreenAction.UNSAVE,
        screen_type: screenType ?? getScreenType(),
        settings: { category: '', id: slug },
      });
    },
    [isAuthorized, queryClient, queryKey, mutation]
  );

  const isSaved = useCallback(
    (slug: string) => savedSlugs.has(slug),
    [savedSlugs]
  );

  return { savedSlugs, isSaved, toggleSaved, isLoading };
};

export default useSavedTemplates;
